'use client';

import { useState, useEffect } from 'react';
import { RefreshCw, ArrowRightLeft, TrendingUp } from 'lucide-react';

interface ExchangeRates {
  base: string;
  date: string;
  rates: Record<string, number>;
}

// 主要货币配置
const MAJOR_CURRENCIES = [
  { code: 'USD', name: '美元', flag: '🇺🇸' },
  { code: 'EUR', name: '欧元', flag: '🇪🇺' },
  { code: 'GBP', name: '英镑', flag: '🇬🇧' },
  { code: 'JPY', name: '日元', flag: '🇯🇵' },
  { code: 'HKD', name: '港币', flag: '🇭🇰' },
  { code: 'SGD', name: '新加坡元', flag: '🇸🇬' },
  { code: 'CAD', name: '加元', flag: '🇨🇦' },
  { code: 'AUD', name: '澳元', flag: '🇦🇺' },
];

export default function ExchangeRateWidget() {
  const [rates, setRates] = useState<ExchangeRates | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  // 模式: 'toCNY' = 外币换人民币, 'fromCNY' = 人民币换外币
  const [mode, setMode] = useState<'toCNY' | 'fromCNY'>('toCNY');

  const fetchRates = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/exchange-rates', {
        cache: 'no-store',
      });
      const data = await response.json();
      if (!response.ok || !data?.success || !data?.rates) {
        throw new Error(data?.error || '获取汇率失败');
      }
      setRates(data.rates);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取汇率失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
    // 每10分钟刷新一次
    const interval = setInterval(fetchRates, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // 计算汇率
  const calculateRate = (currencyCode: string): number | null => {
    if (!rates?.rates) return null;

    const cnyRate = rates.rates['CNY'];
    const currencyRate = rates.rates[currencyCode];

    if (!cnyRate || !currencyRate) return null;

    if (mode === 'toCNY') {
      // 1 外币 = ? 人民币
      return cnyRate / currencyRate;
    } else {
      // 1 人民币 = ? 外币
      return currencyRate / cnyRate;
    }
  };

  const formatRate = (rate: number | null): string => {
    if (rate === null) return '-';
    if (rate < 0.01) return rate.toFixed(6);
    if (rate < 1) return rate.toFixed(4);
    return rate.toFixed(4);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">实时汇率</h2>
        </div>
        <div className="flex items-center gap-2">
          {/* 切换模式按钮 */}
          <button
            onClick={() => setMode(mode === 'toCNY' ? 'fromCNY' : 'toCNY')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="切换换算方向"
          >
            <ArrowRightLeft className="w-4 h-4" />
            <span className="hidden sm:inline">
              {mode === 'toCNY' ? '外币→人民币' : '人民币→外币'}
            </span>
          </button>
          {/* 刷新按钮 */}
          <button
            onClick={fetchRates}
            disabled={loading}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="刷新汇率"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error ? (
        <div className="text-center py-4">
          <p className="text-red-500 text-sm">{error}</p>
          <button
            onClick={fetchRates}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            重试
          </button>
        </div>
      ) : (
        <>
          {/* 当前模式说明 */}
          <div className="mb-3 px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-600">
            {mode === 'toCNY' ? (
              <span>1 外币 = ? 人民币 (CNY)</span>
            ) : (
              <span>1 人民币 (CNY) = ? 外币</span>
            )}
          </div>

          {/* 汇率列表 */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {MAJOR_CURRENCIES.map((currency) => {
              const rate = calculateRate(currency.code);
              return (
                <div
                  key={currency.code}
                  className="flex flex-col p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{currency.flag}</span>
                    <span className="font-medium text-gray-900">{currency.code}</span>
                  </div>
                  <div className="text-xs text-gray-500 mb-1">{currency.name}</div>
                  <div className="text-lg font-semibold text-emerald-600">
                    {loading ? (
                      <span className="text-gray-400">...</span>
                    ) : (
                      formatRate(rate)
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 更新时间 */}
          {lastUpdated && (
            <div className="mt-4 text-xs text-gray-400 text-right">
              更新时间: {lastUpdated.toLocaleTimeString('zh-CN')}
              {rates?.date && <span className="ml-2">数据日期: {rates.date}</span>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
